import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef } from 'react';
import { styled, Column, ButtonColumn, Button, Header, AppText, AppIcon, Row, SafariLandscapeFullHeightHack, } from '@streem/ui-react';
import { useRoomSession } from '../../../context/room_session';
import { useObservable } from '../../../hooks/rxjs';
import { invariant } from '@streem/toolbox';
import { CameraSubject } from 'streem-sdk-protobuf';
import { takeFirstEmitted } from '../../../util/take_first_emitted';
const TIMEOUT_MS_FLIP_CAMERA_REQUEST = 15000;
export const RequestCameraFlip = ({ open, setOpen }) => {
    const timeoutRef = useRef();
    const roomSession = useRoomSession();
    const [request] = useObservable(roomSession.camera.latestRequest);
    const clearTimeOutFlipCamera = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = undefined;
        }
    };
    const handleAbortCameraFlip = useCallback(() => {
        invariant(request, 'Attempted to abort undefined request.');
        roomSession.camera.abortCameraSubjectChangeRequest(request.id);
        setOpen(false);
    }, [request, roomSession.camera, setOpen]);
    useEffect(() => {
        clearTimeOutFlipCamera();
        if (request?.id !== undefined) {
            setOpen(true);
            timeoutRef.current = setTimeout(() => {
                handleAbortCameraFlip();
            }, TIMEOUT_MS_FLIP_CAMERA_REQUEST);
        }
    }, [request?.id, setOpen, handleAbortCameraFlip]);
    const handleRejectCameraFlip = useCallback(() => {
        invariant(request, 'Attempted to reject undefined request.');
        roomSession.camera.rejectCameraSubjectChangeRequest(request.id);
        setOpen(false);
        clearTimeOutFlipCamera();
    }, [request, roomSession.camera, setOpen]);
    const handleFulfillCameraFlip = useCallback(async () => {
        invariant(request, 'Attempted to fulfill undefined request.');
        roomSession.camera.fulfillCameraSubjectChangeRequest(request.id, request.requestedCameraSubject ?? 0);
        const localVideo = await takeFirstEmitted(roomSession.media.localVideoController);
        await localVideo?.flipCamera(request.requestedCameraSubject ?? 0);
        localVideo?.setEnabled(true);
        setOpen(false);
        clearTimeOutFlipCamera();
    }, [request, roomSession.camera, roomSession.media.localVideoController, setOpen]);
    let headerText = "Let's chat face-to-face";
    let copyText = 'Your rear camera will stop and the front camera will start.';
    let primaryButtonText = 'Start Front Camera';
    let secondaryButtonText = 'No, Thanks';
    if (request && request.requestedCameraSubject !== CameraSubject.CAMERA_SUBJECT_PERSON) {
        headerText = 'Can I see your space?';
        copyText = 'Your front camera will stop and the rear camera will start.';
        primaryButtonText = 'Start Rear Camera';
        secondaryButtonText = 'No, Thanks';
    }
    return (_jsx(_Fragment, { children: open && (_jsx(DarkTransparentOverlay, { children: _jsx(Column, { style: {
                    height: '100%',
                    width: '100%',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                }, children: _jsxs(Column, { style: { width: '75%' }, children: [_jsx(Header, { color: "white", bold: true, style: { marginBottom: '6px' }, children: headerText }), _jsx(AppText, { color: "white", children: copyText }), _jsxs(ButtonColumn, { style: {
                                width: '100%',
                                marginBottom: '48px',
                                marginTop: '16px',
                            }, children: [_jsx(Button, { mobile: true, variant: "primary", onClick: handleFulfillCameraFlip, "data-testid": "fulfill-camera-flip", children: _jsxs(Row, { style: { alignItems: 'center' }, children: [_jsx(AppIcon, { name: "CameraFlipIcon", style: { marginRight: '8px' } }), primaryButtonText] }) }), _jsx(Button, { variant: "transparent", mobile: true, onClick: handleRejectCameraFlip, "data-testid": "reject-camera-flip", children: secondaryButtonText })] })] }) }) })) }));
};
const DarkTransparentOverlay = styled.div `
    background-color: rgba(0, 0, 0, 0.7);
    z-index: ${({ theme }) => theme.layers.modal};
    position: absolute;
    width: 100%;
    height: 100%;
    ${SafariLandscapeFullHeightHack}
`;
//# sourceMappingURL=request_camera_flip.js.map